/**
 * Production adapter wiring the React-free boolean ports
 * ({@link BooleanDeps}) to the live engine and the structure-creation
 * choreography. The GPU/engine is a SYSTEM BOUNDARY: this glue is verified by
 * the manual demo + the mandatory HITL human safety review for this slice,
 * not by unit tests — the unit-tested safety core consumes these ports
 * through deterministic fakes.
 *
 * Geometry uses the SAME proven engine entry point as the toolbar boolean
 * (`SDFOperations.applyBooleanToNewTexture`, a pure binary combine returning a
 * NEW texture, mutating no ROI), so the agent boolean path cannot diverge
 * geometrically. The shared overlay/release/create wiring lives in
 * {@link makeEngineDeps}; this file holds only the op-specific apply closure
 * and the undo-history label. The op→engine enum mapping is the locked safety
 * choke-point applied upstream (golden-tested per op); here the token is
 * mapped to the value-identical `BooleanOperation` enum and passed through.
 */
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import type { Environment } from '~/engine/data/Environment';
import {
    BooleanOperation,
    SDFOperations,
} from '~/engine/util/algorithms/sdf/SDFOperations';
import type { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { useApplyComputedGeometry } from '~/hooks/structure-set/useApplyComputedGeometry';
import { environmentState } from '~/state/atoms/engineAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { makeEngineDeps, toComputedGeometry } from '../core';
import type { ComputedGeometry } from '../core';
import type {
    BooleanDeps,
    BooleanEngineOperand,
    EngineBooleanOp,
} from './pipeline';

export function useBooleanEngineDeps(): () => BooleanDeps {
    const env = useRecoilValue(environmentState);
    const structureSet = useRecoilValue(currentStructureSetState);
    const applyGeometry = useApplyComputedGeometry();

    return useCallback(
        (): BooleanDeps =>
            makeEngineDeps(env, structureSet, applyGeometry, {
                label: 'Boolean operation',
                geometry: (env: Environment | null) => {
                    // The locked op→engine enum mapping (the safety core
                    // already emits the token; this is the single
                    // engine-coupled translation, value-identical to the
                    // string enum).
                    const TO_ENGINE: Readonly<
                        Record<EngineBooleanOp, BooleanOperation>
                    > = {
                        or: BooleanOperation.OR,
                        and: BooleanOperation.AND,
                        not: BooleanOperation.AND_NOT,
                        xor: BooleanOperation.XOR,
                    };
                    const textureOf = (
                        operand: BooleanEngineOperand
                    ): ContourTexture => {
                        if (operand.kind === 'step') {
                            return operand.handle as ContourTexture;
                        }
                        const tex = env?.getRoi(operand.roiNumber)?.texture;
                        if (!tex) {
                            throw new Error(
                                'a boolean operand has no contour data'
                            );
                        }
                        return tex;
                    };
                    return {
                        async applyBoolean(
                            op: EngineBooleanOp,
                            left: BooleanEngineOperand,
                            right: BooleanEngineOperand
                        ): Promise<ComputedGeometry> {
                            const result =
                                SDFOperations.applyBooleanToNewTexture(
                                    textureOf(left),
                                    textureOf(right),
                                    TO_ENGINE[op]
                                );
                            // Lazy volume (toComputedGeometry): intermediate
                            // combines are never measured, so only the
                            // composite pays the GPU readback.
                            return toComputedGeometry(result);
                        },
                    };
                },
            }),
        [env, structureSet, applyGeometry]
    );
}
